import { keyframes } from '@emotion/react';
import type { IntrinsicSystemProps } from '@hover/blueprint';
import { forwardRef, system } from '@hover/blueprint';
import React from 'react';

const fade = keyframes`
  0% { opacity: 0.9; }
  50%, 80% { opacity: 0.25; }
  100% { opacity: 0.9; }
`;

export interface AnimatedEllipsisProps extends IntrinsicSystemProps<'svg'> {
  count?: number;
  duration?: number;
  gap?: number;
  label: string;
  size?: number;
  shape?: 'circle' | 'square';
}

export const AnimatedEllipsis = forwardRef<AnimatedEllipsisProps, 'svg'>(
  (
    {
      count = 3,
      duration = 1,
      gap = 2,
      label,
      shape = 'square',
      size = 4,
      ...props
    },
    ref,
  ) => {
    const radius = shape === 'square' ? 0 : '100%';

    return (
      <system.svg
        aria-busy
        aria-hidden="false"
        aria-live="polite"
        display="block"
        viewBox={`0 0 ${size * count + gap * (count - 1)} ${size}`}
        xmlns="http://www.w3.org/2000/svg"
        {...props}
        ref={ref}
      >
        <title>{label}</title>
        {Array.from({ length: count }).map((_, index) => {
          const delay = `${(duration / count) * index}s`;

          return (
            <system.rect
              animation={`${fade} ${duration}s ease-in-out ${delay} infinite`}
              fill="currentColor"
              height={size}
              key={index} // eslint-disable-line react/no-array-index-key
              rx={radius}
              ry={radius}
              width={size}
              x={index * gap + index * size}
              y={0}
            />
          );
        })}
      </system.svg>
    );
  },
);
